import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { productGuidesData } from '../data/productGuidesData';

function ProductGuidesLibrary() {
  // Group guides by lens
  const guidesByLens = productGuidesData.reduce((acc, guide) => {
    if (!acc[guide.lensId]) {
      acc[guide.lensId] = {
        lensLabel: guide.lensLabel,
        lensId: guide.lensId,
        guides: []
      };
    }
    acc[guide.lensId].guides.push(guide);
    return acc;
  }, {});

  const lensGroups = Object.values(guidesByLens);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Product Guides - Find Clean Foods | FoodLens AI</title>
        <meta name="description" content="Browse our comprehensive product guides to find foods without seed oils, artificial dyes, preservatives, and artificial sweeteners. Science-backed recommendations." />
        <meta name="keywords" content="seed oil free products, dye free food, preservative free food, clean eating guide, healthy food alternatives" />
        <link rel="canonical" href="https://foodlens-ai.com/guides" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="font-display text-4xl sm:text-5xl text-charcoal mb-4">
            Product Guides
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find the best products without concerning ingredients. Each guide includes brand recommendations, what to look for, and answers to common questions.
          </p>
        </header>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          <div className="bg-surface border border-sage-100 rounded-xl p-4 text-center">
            <div className="text-3xl font-display text-sage-600">{productGuidesData.length}</div>
            <div className="text-sm text-gray-500">Product Guides</div>
          </div>
          <div className="bg-surface border border-sage-100 rounded-xl p-4 text-center">
            <div className="text-3xl font-display text-sage-600">{lensGroups.length}</div>
            <div className="text-sm text-gray-500">Ingredient Categories</div>
          </div>
          <div className="bg-surface border border-sage-100 rounded-xl p-4 text-center">
            <div className="text-3xl font-display text-sage-600">
              {productGuidesData.reduce((acc, g) => acc + g.products.length, 0)}
            </div>
            <div className="text-sm text-gray-500">Products Reviewed</div>
          </div>
          <div className="bg-surface border border-sage-100 rounded-xl p-4 text-center">
            <div className="text-3xl font-display text-sage-600">
              {productGuidesData.reduce((acc, g) => acc + g.faqs.length, 0)}
            </div>
            <div className="text-sm text-gray-500">FAQs Answered</div>
          </div>
        </div>

        {/* Guides by Lens */}
        {lensGroups.map((group) => (
          <section key={group.lensId} className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-charcoal">
                {group.lensLabel}-Free Products
              </h2>
              <Link
                to={`/lenses/${group.lensId}`}
                className="text-sm text-sage-600 hover:text-sage-700 font-medium"
              >
                Learn about {group.lensLabel} →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {group.guides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/${guide.slug}`}
                  className="p-5 bg-surface border border-sage-100 rounded-xl hover:shadow-soft hover:-translate-y-1 transition-all group"
                >
                  <h3 className="font-semibold text-charcoal mb-2 group-hover:text-sage-600 transition-colors">
                    {guide.categoryLabel}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {guide.intro.substring(0, 80)}...
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{guide.products.length} products</span>
                    <span>{guide.faqs.length} FAQs</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="mt-16 p-8 bg-gradient-sage rounded-2xl text-white text-center">
          <h2 className="font-display text-2xl mb-4">
            Scan Any Product Instantly
          </h2>
          <p className="text-lg text-white/90 mb-6 max-w-xl mx-auto">
            Can't find a product in our guides? FoodLens AI scans any food label and identifies concerning ingredients in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
            >
              Download FoodLens AI
            </Link>
            <Link
              to="/extension"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-medium rounded-xl border border-white/30 hover:bg-white/20 transition-colors"
            >
              Get Browser Extension
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ProductGuidesLibrary;
